import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Label, Button } from 'reactstrap';
import { FundRequestContext } from '~/context/fundrequests';
import { BlueHorizontalLine } from '~/shared/components/icons';
import { FundRequestNote } from '~/types/fundrequests';
import moment from 'moment';
import hhlAdminIcon from '~/shared/img/hhl-admin-icon.png';
import { addClientFundRequestNote, markClientFundRequestNoteRead } from '~/services/fundrequests';
import { FaCheck } from 'react-icons/fa';

const NotesSection = () => {
    const [{ fundRequest }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [noteText, setNoteText] = useState('');
    const [saving, setSaving] = useState(false);
    const maxNoteLength = 800;
    const characterCount = noteText.length;
    const isOverLimit = characterCount > maxNoteLength;

    const notes = useMemo(() => fundRequest?.notes || [], [fundRequest]);
    const sortedNotes = useMemo(() => {
        const arr = [...notes];
        arr.sort((a: FundRequestNote, b: FundRequestNote) => {
            const ad = new Date(a.created_date).getTime();
            const bd = new Date(b.created_date).getTime();
            if (ad !== bd) return ad - bd;
            return (a.id || 0) - (b.id || 0);
        });
        return arr;
    }, [notes]);
    const noteCount = notes.length;
    const listRef = useRef<HTMLDivElement | null>(null);

    // Scroll to bottom to show latest note by default and when list updates
    useEffect(() => {
        if (listRef.current) {
            // Use requestAnimationFrame to ensure layout is ready
            requestAnimationFrame(() => {
                if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                }
            });
        }
    }, [sortedNotes.length]);

    // Always show the notes interface, even if there are no notes yet

    const getNoteStyles = (note: FundRequestNote) => {
        const status = note?.fund_request_status?.value;
        const isAdmin = !note.is_client_note;
        // Admin notes: green by default, red if needs_info. Client notes: light blue.
        if (isAdmin) {
            if (status === 'needs_info') {
                return {
                    backgroundColor: 'rgba(156, 51, 83, 0.10)',
                    border: '1px solid #9c3353',
                    textColor: '#333333',
                    badge: {
                        text: 'Needs Info',
                        bg: '#9c3353',
                        color: '#FFFFFF',
                        border: '1px solid #9c3353',
                    },
                };
            }
            return {
                backgroundColor: '#F5FAEB',
                border: '1px solid #AFC12D',
                textColor: '#333333',
                badge: null,
            };
        }
        return {
            backgroundColor: '#F0F0F0',
            border: '1px solid #E0E0E0',
            textColor: '#333333',
            badge: null,
        };
    };

    return (
        <>
            <Label style={{ fontSize: '1rem' }}>Notes</Label>
            <BlueHorizontalLine />
            {/* Teal scrollbar styling for notes list */}
            <style>
                {`
                .notes-scroll { scrollbar-color: #00AEB3 #E5F7F7; }
                .notes-scroll::-webkit-scrollbar { width: 8px; }
                .notes-scroll::-webkit-scrollbar-track { background: #E5F7F7; border-radius: 8px; }
                .notes-scroll::-webkit-scrollbar-thumb { background-color: #00AEB3; border-radius: 8px; border: 2px solid #E5F7F7; }
                `}
            </style>
            <div
                ref={listRef}
                className='notes-scroll'
                style={{ marginTop: '1rem', maxHeight: 320, overflowY: 'auto', paddingRight: 6 }}
            >
                {sortedNotes.map((note: FundRequestNote) => {
                    const styles = getNoteStyles(note);
                    return (
                        <div key={note.id} style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {note.created_by?.name || (note.is_client_note ? 'Client' : 'Admin')}
                                    {!note.is_client_note && (
                                        <img src={hhlAdminIcon} alt='HHL' style={{ width: 16, height: 16, borderRadius: '50%' }} />
                                    )}
                                </div>
                                <div style={{ fontSize: 12, color: '#6c757d' }}>
                                    {moment(note.created_date).format('M/D/YY')}
                                </div>
                            </div>
                            <div
                                style={{
                                    backgroundColor: styles.backgroundColor,
                                    border: styles.border,
                                    borderRadius: 8,
                                    padding: '12px',
                                }}
                            >
                                {styles.badge && (
                                    <div
                                        style={{
                                            display: 'inline-block',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            padding: '3px 8px',
                                            borderRadius: 999,
                                            marginBottom: 8,
                                            backgroundColor: styles.badge.bg,
                                            color: styles.badge.color,
                                            border: styles.badge.border,
                                        }}
                                    >
                                        {styles.badge.text}
                                    </div>
                                )}
                                <p
                                    style={{
                                        margin: 0,
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.4,
                                        color: (styles as any).textColor || '#333',
                                        fontSize: 14,
                                        marginBottom: !note.is_client_note ? 8 : 0,
                                    }}
                                >
                                    {note.note}
                                </p>
                                {!note.is_client_note && (
                                    <div style={{ marginTop: 8, textAlign: 'right' }}>
                                        {note.client_read ? (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    fontSize: 11,
                                                    color: '#00AEB3',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                <FaCheck size={12} /> Read
                                            </span>
                                        ) : (
                                            <button
                                                style={{
                                                    fontSize: 11,
                                                    padding: '2px 8px',
                                                    backgroundColor: 'transparent',
                                                    color: '#00AEB3',
                                                    border: 'none',
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                    fontWeight: 500,
                                                }}
                                                onClick={async () => {
                                                    if (!fundRequest) return;
                                                    try {
                                                        await markClientFundRequestNoteRead(
                                                            note.id,
                                                            String(fundRequest.campaign)
                                                        );
                                                        // Update the note in context
                                                        const updatedNotes = fundRequest.notes.map((n) =>
                                                            n.id === note.id ? { ...n, client_read: true } : n
                                                        );
                                                        fundRequestDispatch({
                                                            type: 'set_fund_request',
                                                            payload: { ...fundRequest, notes: updatedNotes },
                                                        });
                                                    } catch (e) {
                                                        console.error('Failed to mark note as read', e);
                                                    }
                                                }}
                                            >
                                                Mark as Read
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Add note input */}
            <div style={{ marginTop: '1rem' }}>
                <Label style={{ fontSize: '0.9rem' }}>Add a note</Label>
                <textarea
                    rows={2}
                    className={`form-control ${isOverLimit ? 'is-invalid' : ''}`}
                    placeholder='Type your note here...'
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    maxLength={maxNoteLength}
                    style={{
                        padding: '6px 8px',
                        fontSize: '0.875rem',
                        lineHeight: 1.2,
                        minHeight: '36px',
                        borderRadius: 6,
                    }}
                />
                <div className='d-flex justify-content-between mt-1'>
                    <div>
                        {isOverLimit && (
                            <small className='text-danger'>Note must be {maxNoteLength} characters or less</small>
                        )}
                    </div>
                    <small className={`text-muted ${isOverLimit ? 'text-danger' : ''}`}>
                        {characterCount}/{maxNoteLength} characters
                    </small>
                </div>
                <div className='mt-2'>
                    <small className='text-muted d-block mb-2'>Please allow up to two to three business days for a response.</small>
                    <Button
                        color='primary'
                        size='sm'
                        disabled={saving || !noteText.trim()}
                        onClick={async () => {
                            if (!fundRequest?.id) return;
                            try {
                                setSaving(true);
                                const resp = await addClientFundRequestNote(
                                    String(fundRequest.id),
                                    noteText.trim(),
                                    String(fundRequest.campaign)
                                );
                                // Update context fund request with new note appended
                                const updated = {
                                    ...fundRequest,
                                    notes: [...(fundRequest.notes || []), resp],
                                };
                                fundRequestDispatch({ type: 'set_fund_request', payload: updated });
                                setNoteText('');
                            } catch (e) {
                                console.log('Failed to add note', e);
                            } finally {
                                setSaving(false);
                            }
                        }}
                    >
                        {saving ? 'Sending…' : 'Send'}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default NotesSection;
